<template>
  <div class="ga-overlay" @click.self="$emit('close')">
    <div class="ga-container-card">
      <!-- Header -->
      <div class="ga-header">
        <div class="ga-header-content">
          <div class="ga-header-left">
            <div class="ga-game-mode-badge">
              <i class="fa fa-th-large"></i>
            </div>
            <span class="ga-header-title">Analysis</span>
          </div>
          <div class="ga-header-right">
            <i class="fa fa-times ga-close-btn" @click="$emit('close')"></i>
          </div>
        </div>
      </div>

      <div v-if="!showDemoVideoAnalysis && !showWrongAnswerEvaluation && !showMindsetEvaluation" class="ga-body">
        <div v-if="isLoading" class="ga-loading-overlay" style="display: flex; justify-content: center; align-items: center; min-height: 400px;">
          <i class="fa fa-spinner fa-spin fa-3x" style="color: #3b82f6;"></i>
        </div>
        <div v-else class="ga-content-wrapper">
          <!-- Alert / Warning Banner -->
          <div class="ga-alert-banner">
            <i class="fa fa-exclamation-triangle ga-alert-icon"></i>
            <span class="ga-alert-text">Payment for this session will be released after all required reviews are completed</span>
          </div>

          <!-- Questions Overview Card -->
          <div class="ga-overview-card">
            <div class="ga-overview-info">
              <div class="ga-badge-num">01</div>
              <div>
                <h2 class="ga-overview-title">Questions Overview</h2>
                <p class="ga-overview-subtitle">Review performance overview for each question</p>
              </div>
            </div>
            
            <div class="ga-stats-container">
              <div class="ga-stat-item">
                <span class="ga-stat-num">{{ totalQuestionsVal }}</span>
                <span class="ga-stat-label">TOTAL QUESTIONS</span>
              </div>
              <div class="ga-stat-divider"></div>
              <div class="ga-stat-item">
                <span class="ga-stat-num">{{ attemptedVal }}%</span>
                <span class="ga-stat-label">ATTEMPTED</span>
              </div>
              <div class="ga-stat-divider"></div>
              <div class="ga-stat-item">
                <span class="ga-stat-num text-success">{{ correctVal }}%</span>
                <span class="ga-stat-label">CORRECT ANSWERS</span>
              </div>
              <div class="ga-stat-divider"></div>
              <div class="ga-stat-item">
                <span class="ga-stat-num text-danger">{{ incorrectVal }}%</span>
                <span class="ga-stat-label">INCORRECT ANSWERS</span>
              </div>
            </div>

            <!-- <div class="ga-overview-actions">
              <button class="ga-btn-outline" @click="showSummaryList = !showSummaryList">
                {{ showSummaryList ? 'Hide Summary' : 'View Summary' }}
              </button>
            </div> -->
          </div>

          <!-- Questions Overview Detail List (shown if showSummaryList is true) -->
          <div v-if="showSummaryList" class="ga-slide-list-section" style="margin-top: 24px; margin-bottom: 24px;">
            <div class="ga-section-header" style="margin-bottom: 20px;">
              <h3>Question Breakdown</h3>
              <p>Detailed performance stats and demo status for each slide</p>
            </div>
            
            <div class="dva-grid" style="padding: 0; margin-top: 16px;">
              <div v-for="(slide, idx) in displaySlides" :key="slide.id || idx" class="gt-dashboard-card gt-cpa-card-white">
                <!-- Card Header -->
                <div class="gt-dashboard-card-header gt-cpa-card-header-white">
                  <span class="gt-cpa-card-title">Question {{ idx + 1 }}</span>
                  <div class="gt-cpa-header-stats">
                    <span class="gt-stat-attempted">{{ getPercentAttempted(slide) }}% Attempted</span>
                    <span class="gt-stat-stuck">{{ getPercentStuck(slide) }}% Stuck</span>
                    <span class="gt-stat-wrong">{{ getPercentWrong(slide) }}% Wrong</span>
                  </div>
                </div>

                <!-- Card Body -->
                <div class="gt-dashboard-card-body">
                  <!-- Image if normal slide (Non-CPA) -->
                  <template v-if="!isCpaQuestion(slide)">
                    <div style="padding: 16px; background: #ffffff; text-align: center; flex: 1; display: flex; flex-direction: column; justify-content: center;">
                      <div
                        v-if="slide.pic"
                        class="gt-slide-image"
                        :style="{
                          backgroundImage: 'url(' + Fn.hashToUrl(slide.pic) + ')',
                          minHeight: '180px',
                          backgroundSize: 'contain',
                          backgroundRepeat: 'no-repeat',
                          backgroundPosition: 'center',
                          background: '#f1f5f9',
                          borderRadius: '8px'
                        }"></div>
                      <div v-else style="min-height: 180px; display: flex; justify-content: center; align-items: center; color: #94a3b8; background: #f1f5f9; border-radius: 8px;">
                        <i class="fa fa-image" style="font-size: 32px;"></i>
                      </div>
                    </div>
                    <div style="padding: 16px 20px; display: flex; justify-content: flex-end; align-items: center; gap: 8px; border-top: 1px solid #f1f5f9;">
                      <button class="gt-cpa-view-btn" @click.stop="previewImage(slide.pic)">
                        <i class="fa fa-eye" style="margin-right: 4px;"></i> VIEW
                      </button>
                      <template v-if="hasPostGameDemo(slide)">
                        <button
                          class="gt-cpa-view-btn"
                          style="color: #64748b; border-color: #cbd5e1;"
                          @click.stop="handleShowDemo(slide, null, true)">
                          RE-DO DEMO
                        </button>
                      </template>
                      <template v-else>
                        <button
                          class="gt-cpa-view-btn"
                          style="color: #000000; border-color: #000000;"
                          @click.stop="handleShowDemo(slide, null)">
                          SHOW DEMO
                        </button>
                      </template>
                      <i v-if="hasPostGameDemo(slide)" class="fa fa-check-circle" style="color: #10b981; font-size: 14px"></i>
                    </div>
                  </template>

                  <!-- CPA Question Details (Bloom Table) -->
                  <template v-else>
                    <div style="position: relative; flex: 1; min-height: 0; display: flex; flex-direction: column">
                      <div class="gt-bloom-table-container gt-cpa-table-container">
                        <div class="gt-bloom-table gt-cpa-table">
                          <div class="gt-bloom-header gt-cpa-table-header" style="position: relative">
                            <div
                              style="
                                flex: 2.5;
                                min-width: 135px;
                                padding-left: 12px;
                                padding-right: 4px;
                                position: sticky;
                                left: 0;
                                background: #ffffff;
                                z-index: 2;
                              ">
                              BLOOM LEVEL
                            </div>
                            <div style="flex: 1.5; min-width: 80px; text-align: center">ATTEMPTED</div>
                            <div style="flex: 1.5; min-width: 80px; text-align: center">STUCK</div>
                            <div style="flex: 1.5; min-width: 80px; text-align: center">WRONG</div>
                            <div style="flex: 2.5; min-width: 120px"></div>
                          </div>
                          <div class="gt-bloom-rows gt-cpa-rows">
                            <div v-for="(stat, sIdx) in getCpaBloomStats(slide)" :key="sIdx" class="gt-bloom-row gt-cpa-row" style="position: relative">
                              <div
                                class="gt-cpa-col-level"
                                style="
                                  flex: 2.5;
                                  min-width: 135px;
                                  padding-left: 12px;
                                  padding-right: 4px;
                                  position: sticky;
                                  left: 0;
                                  background: #f8fafc;
                                  z-index: 2;
                                  display: flex;
                                  align-items: center;
                                  gap: 6px;
                                ">
                                <div class="gt-cpa-level-badge">{{ stat.levelNum }}</div>
                                <div class="gt-cpa-level-name" style="width: 68px; flex: none">{{ stat.levelNameFormated }}</div>
                              </div>
                              <div class="gt-cpa-col-stat" style="flex: 1.5; min-width: 80px; color: #1e293b; font-weight: bold; text-align: center">
                                {{ stat.attempt }}/{{ attendedStudentCount }}
                              </div>
                              <div class="gt-cpa-col-stat" style="flex: 1.5; min-width: 80px; color: #eab308; font-weight: bold; text-align: center">
                                {{ stat.stuck }}/{{ attendedStudentCount }}
                              </div>
                              <div class="gt-cpa-col-stat" style="flex: 1.5; min-width: 80px; color: #ef4444; font-weight: bold; text-align: center">
                                {{ stat.wrong }}/{{ attendedStudentCount }}
                              </div>
                              <div
                                style="
                                  flex: 2.5;
                                  min-width: 120px;
                                  text-align: right;
                                  padding-right: 12px;
                                  display: flex;
                                  justify-content: flex-end;
                                  align-items: center;
                                  gap: 8px;
                                ">
                                 <template v-if="stat.hasDemo">
                                   <button
                                     class="gt-cpa-view-btn"
                                     style="color: #64748b; border-color: #cbd5e1;"
                                     @click.stop="handleShowDemo(slide, stat, true)">
                                     RE-DO DEMO
                                   </button>
                                 </template>
                                 <template v-else>
                                   <button
                                     class="gt-cpa-view-btn"
                                     style="color: #000000; border-color: #000000;"
                                     @click.stop="handleShowDemo(slide, stat)">
                                     SHOW DEMO
                                   </button>
                                 </template>
                                <i v-if="stat.hasDemo" class="fa fa-check-circle" style="color: #10b981; font-size: 14px"></i>
                                <div v-else style="width: 16px"></div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <!-- Mask to cover horizontal scrollbar under fixed BLOOM column -->
                      <div
                        style="position: absolute; left: 0; bottom: 0; width: 135px; height: 6px; background: #ffffff; z-index: 5; pointer-events: none"></div>
                    </div>
                  </template>
                </div>
              </div>
            </div>
          </div>

          

          <!-- Required Review Tasks Heading -->
          <div class="ga-tasks-header">
            <div class="ga-tasks-header-row">
              <h2 class="ga-tasks-title">Required Review Tasks</h2>
              <div class="ga-tasks-count">
                <span class="text-success ">{{ completedTasksCount }}</span> of <span>3</span> tasks completed
              </div>
            </div>
            <p class="ga-tasks-subtitle">Complete all required review tasks to receive payment.</p>
          </div>

          <!-- Tasks Cards Grid -->
          <div class="ga-tasks-grid">
            <!-- Card 2: Demo Video -->
            <div class="ga-task-card">
              <div class="ga-task-header">
                <div class="ga-badge-num">02</div>
                <span class="ga-status-badge" :class="demoProgressPercent === 100 ? 'badge-success' : (demoProgressPercent > 0 ? 'badge-warning' : 'badge-gray')">
                  {{ demoProgressPercent === 100 ? 'COMPLETED' : (demoProgressPercent > 0 ? 'IN PROGRESS' : 'NOT STARTED') }}
                </span>
              </div>
              <div class="ga-task-body">
                <h3 class="ga-task-title">Demo Video</h3>
                <p class="ga-task-desc">Provide at least 2 video demos for each question</p>
                
                <div class="ga-progress-section">
                  <div class="ga-progress-labels">
                    <span><span class="ga-fraction">{{ demoCompleted }}/ {{ demoTotal }}</span> completed</span>
                    <span>{{ demoRemaining }} REMAINING</span>
                  </div>
                  <div class="ga-progress-bar">
                    <div class="ga-progress-fill" :class="demoProgressPercent === 100 ? 'bg-success' : (demoProgressPercent > 0 ? 'bg-warning' : 'bg-gray')" :style="{width: demoProgressPercent + '%'}"></div>
                  </div>
                  <div class="ga-progress-footer">
                    <span>{{ demoProgressPercent }}% completed</span>
                  </div>
                </div>
              </div>
              <div class="ga-task-footer">
                <button class="ga-task-btn" @click="handleReviewDemo">
                  Review <span class="ga-btn-arrow">&rarr;</span>
                </button>
              </div>
            </div>

            <!-- Card 3: Wrong Answer Evaluation -->
            <div class="ga-task-card">
              <div class="ga-task-header">
                <div class="ga-badge-num">03</div>
                <span class="ga-status-badge" :class="wrongProgressPercent === 100 ? 'badge-success' : (wrongProgressPercent > 0 ? 'badge-warning' : 'badge-gray')">
                  {{ wrongProgressPercent === 100 ? 'COMPLETED' : (wrongProgressPercent > 0 ? 'IN PROGRESS' : 'NOT STARTED') }}
                </span>
              </div>
              <div class="ga-task-body">
                <h3 class="ga-task-title">Wrong Answer Evaluation</h3>
                <p class="ga-task-desc">Review and elaborate on incorrect answers</p>
                
                <div class="ga-progress-section">
                  <div class="ga-progress-labels">
                    <span>
                      <span class="ga-fraction">{{ wrongEvaluated }}/ {{ wrongTotal }}</span> evaluated
                      <i class="el-icon-info" title="Total no. of wrong answers given by all students" style="margin-left: 4px; cursor: pointer; color: #6b7280;"></i>
                    </span>
                    <span>{{ wrongRemaining }} REMAINING</span>
                  </div>
                  <div class="ga-progress-bar">
                    <div class="ga-progress-fill" :class="wrongProgressPercent === 100 ? 'bg-success' : (wrongProgressPercent > 0 ? 'bg-warning' : 'bg-gray')" :style="{width: wrongProgressPercent + '%'}"></div>
                  </div>
                  <div class="ga-progress-footer">
                    <span>{{ wrongProgressPercent }}% completed</span>
                  </div>
                </div>
              </div>
              <div class="ga-task-footer">
                <button class="ga-task-btn" @click="handleStartWrongEvaluation">
                  Start Evaluation <span class="ga-btn-arrow">&rarr;</span>
                </button>
              </div>
            </div>

            <!-- Card 4: Mindset Evaluation -->
            <div class="ga-task-card">
              <div class="ga-task-header">
                <div class="ga-badge-num">04</div>
                <span class="ga-status-badge" :class="mindsetProgressPercent === 100 ? 'badge-success' : (mindsetProgressPercent > 0 ? 'badge-warning' : 'badge-gray')">
                  {{ mindsetProgressPercent === 100 ? 'COMPLETED' : (mindsetProgressPercent > 0 ? 'IN PROGRESS' : 'NOT STARTED') }}
                </span>
              </div>
              <div class="ga-task-body">
                <h3 class="ga-task-title">Mindset Evaluation</h3>
                <p class="ga-task-desc">Review student mindset reflections.</p>
                
                <div class="ga-progress-section">
                  <div class="ga-progress-labels">
                    <span><span class="ga-fraction">{{ mindsetEvaluated }}/ {{ mindsetTotal }}</span> evaluated</span>
                    <span>{{ mindsetRemaining }} REMAINING</span>
                  </div>
                  <div class="ga-progress-bar">
                    <div class="ga-progress-fill" :class="mindsetProgressPercent === 100 ? 'bg-success' : (mindsetProgressPercent > 0 ? 'bg-warning' : 'bg-gray')" :style="{width: mindsetProgressPercent + '%'}"></div>
                  </div>
                  <div class="ga-progress-footer">
                    <span>{{ mindsetProgressPercent }}% completed</span>
                  </div>
                </div>
              </div>
              <div class="ga-task-footer">
                <button class="ga-task-btn" @click="handleContinueMindsetEvaluation">
                  Continue Evaluation <span class="ga-btn-arrow">&rarr;</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-else-if="showDemoVideoAnalysis" class="ga-body ga-subpage-body">
        <DemoVideoAnalysis
          :slides="slides"
          :responses="responses"
          :analysis="analysis"
          :gameOnRecord="gameOnRecord"
          :activityLogs="activityLogs"
          :existingDemoQuestionIds="existingDemoQuestionIds"
          :loading="isLoading"
          @close="showDemoVideoAnalysis = false"
          @show-demo="handleShowDemoFromChild" />
      </div>
      <div v-else-if="showWrongAnswerEvaluation" class="ga-body ga-subpage-body">
        <WrongAnswerEvaluation :slides="slides" :gameOnRecord="gameOnRecord" :activityLogs="activityLogs" :apiResponses="apiResponses" :apiQuestions="apiQuestions" :reasoningTags="reasoningTags"
          @close="showWrongAnswerEvaluation = false" @refresh-tags="fetchReasoningTags" />
      </div>
      <div v-else-if="showMindsetEvaluation" class="ga-body ga-subpage-body">
        <MindsetEvaluation :gameOnRecord="gameOnRecord" :evaluations="mindsetEvaluations" @close="showMindsetEvaluation = false; fetchMindsetEvaluations()" />
      </div>

    </div>

    <!-- Modals and Popups for Demo Recording/Playback -->
    <StartDemoPopup
      v-if="showStartDemoPopup"
      :type="startDemoType"
      :curQuestion="selectedStudentSlide"
      :cpaStat="activeCpaStat"
      :isRedoDemo="isRedoDemo"
      :replaceRecording.sync="replaceRecording"
      :studentName="selectedStudentName"
      @start="onStartDemo"
      @close="showStartDemoPopup = false" />

    <div v-if="showStudentSlideView" class="gt-overlay" style="position: absolute; inset: 0; z-index: 1000000000">
      <DemoSessionPage
        :curQuestion="selectedStudentSlide"
        :studentId="selectedStudentId"
        :studentName="getStudentName(selectedStudentId)"
        :targetUids="groupDemoUids"
        :roomId="roomId"
        :is-red-case="false"
        :recording-type="currentRecordingType"
        :isPostGameDemo="isPostGameDemo"
        :isRedoDemo="isRedoDemo"
        :gameOnRecord="gameOnRecord"
        :initialCpaStat="activeCpaStat"
        :cpaStatsList="getCpaBloomStats(selectedStudentSlide)"
        :isReviewAnswerMode="false"
        @close="closeStudentSlideView"
        @sent-recording="markLogAsResolved"
        @demo-sent="handleDemoSent" />
    </div>

    <!-- Video Playback Overlay -->
    <div
      v-if="previewVideoUrl"
      class="gt-overlay"
      style="z-index: 1000000001; background: rgba(0, 0, 0, 0.95); display: flex; justify-content: center; align-items: center;"
      @click="previewVideoUrl = null">
      <div style="position: absolute; top: 20px; right: 20px; color: #fff; font-size: 24px; cursor: pointer;">
        <i class="fa fa-times"></i>
      </div>
      <video :src="previewVideoUrl" controls autoplay style="max-width: 100%; max-height: 80vh; display: block"></video>
    </div>
    
  </div>
</template>

<script>
import DemoVideoAnalysis from './DemoVideoAnalysis.vue'
import WrongAnswerEvaluation from './WrongAnswerEvaluation.vue'
import StartDemoPopup from './StartDemoPopup.vue'
import DemoSessionPage from './DemoSessionPage.vue'
import MindsetEvaluation from './MindsetEvaluation.vue'
import { mapState } from 'vuex'

export default {
  name: 'GameAnalyticsModal',
  components: {
    DemoVideoAnalysis,
    WrongAnswerEvaluation,
    StartDemoPopup,
    DemoSessionPage,
    MindsetEvaluation
  },
  props: {
    slides: {type: Array, default: () => []},
    responses: {type: Array, default: () => []},
    analysis: {type: Object, default: () => null},
    gameOnRecord: {type: Object, default: () => null},
    allGameOnRecords: {type: Array, default: () => []},
    openEvaluate: {type: Function},
  },
  data() {
    return {
      showSummaryList: false,
      showDemoVideoAnalysis: false,
      showWrongAnswerEvaluation: false,
      showMindsetEvaluation: false,
      activityLogs: [],
      existingDemoQuestionIds: [],
      apiResponses: [],
      apiQuestions: [],
      reasoningTags: [],
      mindsetEvaluations: [],

      // Demo recording flow state
      selectedStudentSlide: null,
      selectedStudentId: null,
      selectedStudentName: '',
      activeCpaStat: null,
      isRedoDemo: false,
      replaceRecording: false,
      groupDemoUids: [],
      currentRecordingType: '',
      isPostGameDemo: false,
      startDemoType: '',
      showStartDemoPopup: false,
      showStudentSlideView: false,
      previewVideoUrl: null,
      localGameOnRecord: null,
      localAllGameOnRecords: [],
      isLoading: false,
    }
  },
  created() {
  },
  mounted() {
    document.body.appendChild(this.$el)
  },
  destroyed() {
    if (this.$el && this.$el.parentNode) {
      this.$el.parentNode.removeChild(this.$el)
    }
  },
  watch: {
    gameOnRecord: {
      immediate: true,
      async handler() {
        this.isLoading = true;
        await Promise.all([
          this.fetchAllGameOns(),
          this.fetchLogs(),
          this.fetchExistingDemos(),
          this.fetchWrongEvaluationData(),
          this.fetchMindsetEvaluations()
        ]);
        this.isLoading = false;
      }
    }
  },
  computed: {
    ...mapState(['session', 'user']),
    Fn() {
      return window.Fn || {}
    },
    roomId() {
      return this.session && this.session.sid
    },
    effectiveGameOnRecord() {
      return this.gameOnRecord || this.localGameOnRecord
    },
    isMeetingOngoing() {
      if (!this.session?.lastMeetingHeartbeat) return false
      return Date.now() - this.session.lastMeetingHeartbeat < 30000
    },
    completedGameOnRecords() {
      let list = []
      if (this.localAllGameOnRecords && this.localAllGameOnRecords.length) {
        list = [...this.localAllGameOnRecords]
      } else if (this.allGameOnRecords && this.allGameOnRecords.length) {
        list = [...this.allGameOnRecords]
      } else if (this.effectiveGameOnRecord) {
        list = [this.effectiveGameOnRecord]
      }

      if (this.effectiveGameOnRecord) {
        const effId = this.effectiveGameOnRecord._id || this.effectiveGameOnRecord.id
        if (effId && !list.some((r) => (r._id || r.id) === effId)) {
          list.push(this.effectiveGameOnRecord)
        }
      }
      return list
    },
    allSessionSlides() {
      const slideList = [...(this.slides || [])]
      const existingPageIds = new Set(
        slideList.map((s) => this.getNormId(s.pageId || s.page || s.id)).filter(Boolean)
      )

      const sessionQuestions = this.session?.questions || []

      this.completedGameOnRecords.forEach((rec) => {
        const pageIds = rec.pageIds || []
        pageIds.forEach((pId) => {
          const norm = this.getNormId(pId)
          if (norm && !existingPageIds.has(norm)) {
            existingPageIds.add(norm)
            const q = sessionQuestions.find(
              (sq) =>
                this.getNormId(sq.page) === norm ||
                this.getNormId(sq._id) === norm ||
                this.getNormId(sq.id) === norm
            )
            const pageData = this.$store?.getters?.getPageById
              ? this.$store.getters.getPageById(pId) || this.$store.getters.getPageById(norm)
              : null

            let titleType = ''
            if (pageData?.type === 'video') {
              titleType = 'VIDEO'
            } else if (q) {
              titleType = q.type ? q.type.toUpperCase() : 'TEXT'
              if (q.type === 'choice') titleType = 'MULTIPLE CHOICE'
            }

            slideList.push({
              pageId: norm,
              id: q?._id || q?.id || norm,
              page: pId,
              pic: pageData?.pic || q?.pic || '',
              title: titleType ? `${titleType} QUESTION` : (q?.title || `Question ${slideList.length + 1}`),
              points: q?.score?.points ?? (q?.type === 'choice' && (q.options || []).some((o) => o.on) ? 2 : null),
              questions: q ? [q] : [],
              _displayIdx: String(slideList.length + 1).padStart(2, '0'),
              correctPercent: 0,
              totalResponses: 0,
              difficulty: 'NO DATA',
            })
          }
        })
      })

      return slideList
    },
    displaySlides() {
      if (this.allSessionSlides && this.allSessionSlides.length) {
        return this.allSessionSlides.map((s, idx) => {
          let correctPercent = 0
          let totalResponses = 0
          let diffName = s.points >= 4 ? 'DIFFICULT' : s.points >= 2 ? 'MEDIUM' : 'EASY'

          // Prefer backend 'analysis' object if present
          if (this.analysis && this.analysis.slides) {
            const aSlide = this.analysis.slides.filter((as) => as.pageId === s.pageId)
            if(aSlide?.length > 1){
                // for video session 
                correctPercent = Math.round((aSlide.reduce((acc, curr) => acc + curr.accuracy, 0) / aSlide.length))
                totalResponses = Math.round((aSlide.reduce((acc, curr) => acc + curr.totalResponses, 0) / aSlide.length))
                diffName = correctPercent >= 80 ? 'EASY' : correctPercent >= 50 ? 'MEDIUM' : 'DIFFICULT'
            }
            else if(aSlide?.length) {
              correctPercent = aSlide[0].accuracy || 0
              totalResponses = aSlide[0].totalResponses || 0
              diffName = aSlide[0].difficulty ? aSlide[0].difficulty.toUpperCase() : diffName
            }
          } else {
            // Fallback: Calculate manually from raw responses
            const slideResponses = this.responses.filter((r) => r.page === s.page || r.page === s.id || r.page_id === s.id || r.quizId === s.id)
            if (slideResponses.length > 0) {
              const correctCount = slideResponses.filter(
                (r) =>
                  r.isRight === true || r.correct === true || r.score > 0 || (r.answer && s.answer && JSON.stringify(r.answer) === JSON.stringify(s.answer))
              ).length
              totalResponses = slideResponses.length
              correctPercent = Math.round((correctCount / totalResponses) * 100)
            }
          }

          if (totalResponses === 0) {
            diffName = 'NO DATA'
          }

          return {
            ...s, // keep original fields (pic, _id, page, etc.)
            _displayIdx: String(idx + 1).padStart(2, '0'), // safe display number
            title: s.title || `Slide ${idx + 1}`,
            correctPercent: correctPercent,
            totalResponses: totalResponses,
            difficulty: diffName,
          }
        })
      }
      return []
    },
    avgScore() {
      if (this.analysis && typeof this.analysis.averageScore === 'number') {
        return this.analysis.averageScore
      }
    },
    // Redesigned UI dynamic data
    totalQuestionsVal() {
      return this.allSessionSlides ? this.allSessionSlides.length : 0
    },
    attemptedVal() {
      const records = this.completedGameOnRecords
      if (!records.length || !this.allSessionSlides.length) return 0

      let totalOpportunities = 0
      let totalAttemptedCount = 0

      records.forEach((record) => {
        const members = (record && record.members) || []
        const guestMembers = (record && record.guestMembers) || []
        const allMembers = [...members, ...guestMembers]
        if (allMembers.length === 0) return

        const recordPageIds = (record.pageIds || []).map((p) => this.getNormId(p)).filter(Boolean)
        const recordQuestionIds = (record.questionIds || []).map((q) => this.getNormId(q)).filter(Boolean)

        const gameSlides = this.allSessionSlides.filter((slide) => {
          const sPageId = this.getNormId(slide.pageId || slide.page || slide.id)
          const sId = this.getNormId(slide.id || slide._id)
          if (recordPageIds.length > 0) {
            return recordPageIds.includes(sPageId) || recordPageIds.includes(sId)
          }
          if (recordQuestionIds.length > 0) {
            return recordQuestionIds.includes(sPageId) || recordQuestionIds.includes(sId)
          }
          return true
        })

        const numQuestions = gameSlides.length || recordPageIds.length || 1
        totalOpportunities += allMembers.length * numQuestions

        allMembers.forEach((m) => {
          gameSlides.forEach((slide) => {
            const pageId = slide.pageId || slide.page || slide.id
            const normPageId = this.getNormId(pageId)
            const slideId = slide.id || slide._id
            const normSlideId = this.getNormId(slideId)

            const fullQuestion = this.getFullQuestion(slide)
            const bloomIds = fullQuestion && fullQuestion.bloom_questions ? Object.values(fullQuestion.bloom_questions).map((b) => this.getNormId(b)) : []

            const isAttempted =
              (m.lockedPages && (m.lockedPages.includes(pageId) || (normPageId && m.lockedPages.some((lp) => this.getNormId(lp) === normPageId)))) ||
              (m.lockedQuestionIds &&
                (m.lockedQuestionIds.includes(pageId) ||
                  (normPageId && m.lockedQuestionIds.some((lq) => this.getNormId(lq) === normPageId)) ||
                  (slideId && m.lockedQuestionIds.includes(slideId)) ||
                  (normSlideId && m.lockedQuestionIds.some((lq) => this.getNormId(lq) === normSlideId)) ||
                  bloomIds.some((bId) => m.lockedQuestionIds.some((lq) => this.getNormId(lq) === bId))))
            if (isAttempted) totalAttemptedCount++
          })
        })
      })

      if (totalOpportunities === 0) return 0
      return Math.round((totalAttemptedCount / totalOpportunities) * 100)
    },
    correctVal() {
      const records = this.completedGameOnRecords
      if (!records.length || !this.allSessionSlides.length) return 0

      let totalOpportunities = 0
      let totalCorrectCount = 0

      records.forEach((record) => {
        const members = (record && record.members) || []
        const guestMembers = (record && record.guestMembers) || []
        const allMembers = [...members, ...guestMembers]
        if (allMembers.length === 0) return

        const recordPageIds = (record.pageIds || []).map((p) => this.getNormId(p)).filter(Boolean)
        const recordQuestionIds = (record.questionIds || []).map((q) => this.getNormId(q)).filter(Boolean)

        const gameSlides = this.allSessionSlides.filter((slide) => {
          const sPageId = this.getNormId(slide.pageId || slide.page || slide.id)
          const sId = this.getNormId(slide.id || slide._id)
          if (recordPageIds.length > 0) {
            return recordPageIds.includes(sPageId) || recordPageIds.includes(sId)
          }
          if (recordQuestionIds.length > 0) {
            return recordQuestionIds.includes(sPageId) || recordQuestionIds.includes(sId)
          }
          return true
        })

        const numQuestions = gameSlides.length || recordPageIds.length || 1
        totalOpportunities += allMembers.length * numQuestions

        allMembers.forEach((m) => {
          gameSlides.forEach((slide) => {
            const pageId = slide.pageId || slide.page || slide.id
            const normPageId = this.getNormId(pageId)
            const slideId = slide.id || slide._id
            const normSlideId = this.getNormId(slideId)

            const fullQuestion = this.getFullQuestion(slide)
            const bloomIds = fullQuestion && fullQuestion.bloom_questions ? Object.values(fullQuestion.bloom_questions).map((b) => this.getNormId(b)) : []

            const isCorrect =
              (m.correctPages && (m.correctPages.includes(pageId) || (normPageId && m.correctPages.some((cp) => this.getNormId(cp) === normPageId)))) ||
              (m.correctQuestionIds &&
                (m.correctQuestionIds.includes(pageId) ||
                  (normPageId && m.correctQuestionIds.some((cq) => this.getNormId(cq) === normPageId)) ||
                  (slideId && m.correctQuestionIds.includes(slideId)) ||
                  (normSlideId && m.correctQuestionIds.some((cq) => this.getNormId(cq) === normSlideId)) ||
                  bloomIds.some((bId) => m.correctQuestionIds.some((cq) => this.getNormId(cq) === bId))))
            if (isCorrect) totalCorrectCount++
          })
        })
      })

      if (totalOpportunities === 0) return 0
      return Math.round((totalCorrectCount / totalOpportunities) * 100)
    },
    incorrectVal() {
      return Math.max(0, this.attemptedVal - this.correctVal)
    },
    // Tasks data
    demoTotal() {
      if (!this.allSessionSlides || !this.allSessionSlides.length) return 0
      let total = 0
      this.allSessionSlides.forEach((slide) => {
        if (this.isCpaQuestion(slide)) {
          total += 2
        } else {
          total += 1
        }
      })
      return total
    },
    demoCompleted() {
      if (!this.allSessionSlides || !this.allSessionSlides.length || !this.effectiveGameOnRecord) return 0
      let completed = 0
      this.allSessionSlides.forEach((slide) => {
        if (this.isCpaQuestion(slide)) {
          const fullQuestion = this.getFullQuestion(slide)
          let count = 0
          if (fullQuestion && fullQuestion.bloom_questions) {
            Object.values(fullQuestion.bloom_questions).forEach((qId) => {
              if (this.hasPostGameDemo(slide, qId)) count++
            })
          } else {
            const slideId = slide.page || slide.id
            if (this.hasPostGameDemo(slide, slideId)) count++
          }
          completed += Math.min(2, count)
        } else {
          const slideId = slide.page || slide.id
          if (this.hasPostGameDemo(slide, slideId)) completed += 1
        }
      })
      return completed
    },
    demoRemaining() {
      return Math.max(0, this.demoTotal - this.demoCompleted)
    },
    demoProgressPercent() {
      if (this.demoTotal === 0) return 0
      return Math.round((this.demoCompleted / this.demoTotal) * 100)
    },

    getWrongQuestions() {
      const records = this.completedGameOnRecords
      let allMembers = []
      if (records && records.length) {
        const uids = new Set()
        records.forEach(r => {
          const mems = [...(r.members || []), ...(r.guestMembers || [])]
          mems.forEach(m => {
            const id = m.uid || m._id || m.id
            if (id && !uids.has(String(id))) {
              uids.add(String(id))
              allMembers.push(m)
            }
          })
        })
      }
      if (!allMembers.length) {
        const record = this.effectiveGameOnRecord
        allMembers = [...(record?.members || []), ...(record?.guestMembers || [])]
      }
      
      let allWrong = []
      
      allMembers.forEach(m => {
        const uid = m.uid || m._id || m.id
        const studentResponses = this.apiResponses.filter(r => r.uid === uid || r.studentId === uid || r.userId === uid || r.peer === uid || r.name === uid)
        
        const latestAttempts = {}
        studentResponses.forEach(r => {
          const qId = r.questionId || r.page
          if (qId) latestAttempts[qId] = r
        })
        
        Object.keys(latestAttempts).forEach(qId => {
          const latestResponse = latestAttempts[qId]
          if (latestResponse.isCorrect === true) return // Skip correct answers
          
          const qData = this.findQuestionData(qId)
          if (!qData) return // Exclude ghost questions
          
          const hasBloomQuestions = qData.bloom_questions || (qData.cpa && qData.cpa.bloom_questions)
          if (hasBloomQuestions && !qData.isSubQuestion) return // Exclude parent container
          
          allWrong.push({ uid, qId })
        })
      })
      return allWrong
    },
    wrongEvaluated() { 
      const wrongList = this.getWrongQuestions
      let count = 0
      wrongList.forEach(item => {
        const tags = this.reasoningTags.filter(t => 
          (String(t.uid) === String(item.uid) || String(t.studentId) === String(item.uid) || String(t.userId) === String(item.uid)) && 
          (String(t.questionid) === String(item.qId) || String(t.questionId) === String(item.qId))
        )
        if (tags.length > 0) count++
      })
      return count
    },
    wrongTotal() { 
      return this.getWrongQuestions.length
    },
    wrongRemaining() { 
      return Math.max(0, this.wrongTotal - this.wrongEvaluated)
    },
    wrongProgressPercent() { 
      if (this.wrongTotal === 0) return 0
      return Math.round((this.wrongEvaluated / this.wrongTotal) * 100)
    },

    mindsetTotal() { 
      return this.attendedStudentCount;
    },
    mindsetEvaluated() { 
      if (!this.mindsetEvaluations) return 0;
      const evaluatedIds = new Set(this.mindsetEvaluations.map(e => e.studentId));
      return evaluatedIds.size;
    },
    mindsetRemaining() { 
      return Math.max(0, this.mindsetTotal - this.mindsetEvaluated); 
    },
    mindsetProgressPercent() { 
      if (this.mindsetTotal === 0) return 0;
      return Math.round((this.mindsetEvaluated / this.mindsetTotal) * 100); 
    },

    completedTasksCount() {
      let count = 0
      if (this.demoProgressPercent === 100) count++
      if (this.wrongProgressPercent === 100) count++
      if (this.mindsetProgressPercent === 100) count++
      return count
    },
    attendedStudentCount() {
      const uids = new Set()
      this.completedGameOnRecords.forEach((record) => {
        const members = (record && record.members) || []
        const guestMembers = (record && record.guestMembers) || []
        members.forEach((m) => {
          const uid = m.uid || m._id || m.id
          if (uid) uids.add(String(uid))
        })
        guestMembers.forEach((m) => {
          const uid = m.uid || m._id || m.id
          if (uid) uids.add(String(uid))
        })
      })
      if (uids.size > 0) return uids.size

      if (this.$store?.state?.teacher?.rooms?.members?.length > 0) {
        return this.$store.state.teacher.rooms.members.length;
      }
      if (this.responses && this.responses.length) {
        const studentIds = new Set(this.responses.map(r => r.studentId || r.userId || r.peer || r.name || r.uid).filter(Boolean));
        return studentIds.size || 0;
      }
      return 0;
    }
  },

  methods: {
    getNormId(id) {
      if (!id || typeof id !== 'string') return id
      let res = id
      if (res.includes('::')) {
        res = res.split('::')[1]
      }
      return res
    },
    async fetchAllGameOns() {
      const sid = (this.effectiveGameOnRecord && (this.effectiveGameOnRecord.roomId || this.effectiveGameOnRecord.sid)) || (this.session && this.session.sid)
      const sessionId = (this.effectiveGameOnRecord && this.effectiveGameOnRecord.sessionId) || (this.session && this.session._id)
      if (!window.App || (!sid && !sessionId)) return

      try {
        const query = { $limit: 50, $sort: { createdAt: 1 } }
        if (sessionId) {
          query.sessionId = sessionId
        } else if (sid) {
          query.sid = sid
        }
        const res = await App.service('game-on').find({ query })
        const records = Array.isArray(res) ? res : res.data ? res.data : []
        const completed = records.filter((r) => r.status === 2 || (r.status === 1 && r.expiresAt && new Date(r.expiresAt) < new Date()))
        if (this.gameOnRecord && !completed.some((r) => (r._id || r.id) === (this.gameOnRecord._id || this.gameOnRecord.id))) {
          completed.push(this.gameOnRecord)
        }
        this.localAllGameOnRecords = completed
      } catch (e) {
        console.error('Failed to fetch all game-on records:', e)
      }
    },
    getSlideMembers(slide) {
      if (!slide) return []
      const pageId = slide.pageId || slide.page || slide.id
      const normPageId = this.getNormId(pageId)
      const slideId = slide.id || slide._id
      const normSlideId = this.getNormId(slideId)

      let relevantMembers = []
      this.completedGameOnRecords.forEach((record) => {
        const recordPageIds = (record.pageIds || []).map((p) => this.getNormId(p)).filter(Boolean)
        const recordQuestionIds = (record.questionIds || []).map((q) => this.getNormId(q)).filter(Boolean)

        const hasSlide =
          recordPageIds.includes(normPageId) ||
          recordPageIds.includes(normSlideId) ||
          recordQuestionIds.includes(normPageId) ||
          recordQuestionIds.includes(normSlideId)

        if (hasSlide || recordPageIds.length === 0) {
          const members = (record && record.members) || []
          const guestMembers = (record && record.guestMembers) || []
          relevantMembers.push(...members, ...guestMembers)
        }
      })

      if (relevantMembers.length === 0) {
        const record = this.effectiveGameOnRecord
        const members = (record && record.members) || []
        const guestMembers = (record && record.guestMembers) || []
        relevantMembers = [...members, ...guestMembers]
      }
      return relevantMembers
    },
    findQuestionData(pageId) {
      if (!pageId) return null
      
      // 1. Try questions API data first
      let apiFound = this.apiQuestions.find(q => String(q.id) === String(pageId) || String(q._id) === String(pageId) || String(q.page) === String(pageId))
      if (apiFound) {
        return apiFound
      }

      // 2. Try session questions
      const sessionQuestions = this.session?.questions || []
      let found = sessionQuestions.find(q =>
        String(q._id) === String(pageId) || String(q.id) === String(pageId) || String(q.page) === String(pageId)
      )
      if (!found) {
        // Try allSessionSlides or slides
        const slideList = (this.allSessionSlides && this.allSessionSlides.length) ? this.allSessionSlides : (this.slides || [])
        found = slideList.find(s =>
          String(s._id) === String(pageId) || String(s.id) === String(pageId) || String(s.page) === String(pageId) || String(s.pageId) === String(pageId)
        )
      }
      if (found) return found

      // 3. Try sub-questions in bloom_questions
      for (const q of sessionQuestions) {
        const bq = q.bloom_questions || (q.cpa && q.cpa.bloom_questions)
        if (bq) {
          const entry = Object.entries(bq).find(([key, qId]) => String(qId) === String(pageId))
          if (entry) {
            return {
              ...q,
              bloom: [parseInt(entry[0])],
              isSubQuestion: true,
              parentPageId: q._id || q.id || q.page
            }
          }
        }
      }
      const slideList = (this.allSessionSlides && this.allSessionSlides.length) ? this.allSessionSlides : (this.slides || [])
      for (const s of slideList) {
        const bq = s.bloom_questions || (s.cpa && s.cpa.bloom_questions)
        if (bq) {
          const entry = Object.entries(bq).find(([key, qId]) => String(qId) === String(pageId))
          if (entry) {
            return {
              ...s,
              bloom: [parseInt(entry[0])],
              isSubQuestion: true,
              parentPageId: s._id || s.id || s.page || s.pageId
            }
          }
        }
      }
      return null
    },
    getDifficultyColor(diff) {
      if (diff === 'NO DATA') return '#8c99a0'
      if (diff === 'EASY') return '#10b981'
      if (diff === 'MEDIUM') return '#f59e0b'
      if (diff === 'DIFFICULT') return '#ef4444'
      return '#0ea5e9'
    },
    handleReviewDemo() {
      this.showDemoVideoAnalysis = true
    },
    handleStartWrongEvaluation() {
      this.showWrongAnswerEvaluation = true
    },
    handleContinueMindsetEvaluation() {
      this.showMindsetEvaluation = true;
    },
    // Dynamically fetch activity logs from server
    async fetchLogs() {
      const recordId = this.effectiveGameOnRecord && (this.effectiveGameOnRecord._id || this.effectiveGameOnRecord.id)
      if (window.App && recordId) {
        try {
          const res = await App.service('game-on-log').find({
            query: {
              gameId: recordId,
              $limit: 1000,
            },
          })
          this.activityLogs = res.data || res || []
        } catch (e) {
          console.error('Failed to fetch game-on-log:', e)
        }
      }
    },
    async fetchMindsetEvaluations() {
      let record = this.effectiveGameOnRecord
      let allMembers = []
      
      if (record) {
        const members = record.members || []
        const guestMembers = record.guestMembers || []
        allMembers = [...members, ...guestMembers]
      }
      
      if (allMembers.length === 0 && this.$store?.state?.teacher?.rooms?.members) {
        allMembers = this.$store.state.teacher.rooms.members;
      }
      if (allMembers.length === 0 && this.responses && this.responses.length) {
         const uniqueUids = [...new Set(this.responses.map(r => r.studentId || r.userId || r.peer || r.name || r.uid).filter(Boolean))]
         allMembers = uniqueUids.map(uid => ({ id: uid }))
      }

      if (allMembers.length === 0 || !window.App) return;

      const studentIds = allMembers.map(m => String(m.id || m.uid || m._id)).filter(Boolean)
      const sid = (record && (record.sid)) || (this.session && this.session.sid)

      const query = { studentId: { $in: studentIds }, $limit: 1000 }
      if (sid) {
        query.sid = sid
      }

      try {
        const res = await App.service('mindset-evaluation').find({
          query,
        })
        this.mindsetEvaluations = res.data || res || []
      } catch (e) {
        console.error('Failed to fetch mindset evaluations:', e)
        this.mindsetEvaluations = []
      }
    },
    async fetchWrongEvaluationData() {
      let record = this.gameOnRecord
      const sid = (record && (record.roomId || record.sid)) || (this.session && this.session.sid)
      
      if (!record && sid && window.App) {
        try {
          const res = await App.service('game-on').find({
            query: { sid, $limit: 1, $sort: { createdAt: -1 } },
          })
          const records = res.data || res || []
          if (records.length > 0) {
            this.localGameOnRecord = records[0]
            record = this.localGameOnRecord
          }
        } catch(e) {
          console.error('Failed to fetch fallback gameOnRecord', e)
        }
      }

      if (!record || !window.App) return

      try {
        const res = await App.service('response').find({
          query: { sid, $limit: 10000, $sort: { createdAt: 1 } },
        })
        this.apiResponses = res.data || res || []

        const uniqueQIds = [...new Set(this.apiResponses.map(r => r.questionId || r.page).filter(Boolean))]
        if (uniqueQIds.length > 0) {
          const qRes = await App.service('questions').find({
            query: { _id: { $in: uniqueQIds }, $limit: 2000 },
          })
          this.apiQuestions = qRes.data || qRes || []
        }

        await this.fetchReasoningTags()
      } catch (e) {
        console.error('Failed to fetch WAE data:', e)
      }
    },
    async fetchReasoningTags() {
      const record = this.effectiveGameOnRecord
      if (!record || !window.App) return
      const sid = record.roomId || record.sid || (this.session && this.session.sid)
      
      const uniqueQIds = [...new Set(this.apiResponses.map(r => r.questionId || r.page).filter(Boolean))]
      const query = { sid, $limit: 5000 }
      if (uniqueQIds.length > 0) {
        query.questionid = { $in: uniqueQIds }
      }

      try {
        const res = await App.service('student-reasoning-tag').find({ query })
        this.reasoningTags = res.data || res || []
      } catch (e) {
        console.error('Failed to fetch tags:', e)
      }
    },
    getFullQuestion(slide) {
      if (!slide) return null
      const pageId = slide.page || slide.id || slide.pageId
      const session = this.$store && this.$store.state && this.$store.state.session
      const questions = session && session.questions ? session.questions : []
      return questions.find((q) => q.page === pageId || q.id === pageId) || slide
    },
    isCpaQuestion(slide) {
      if (slide.hasOwnProperty('isCpa')) {
        return slide.isCpa
      }
      const fullQuestion = this.getFullQuestion(slide)
      return !!(fullQuestion && fullQuestion.cpa && fullQuestion.cpa.outcome)
    },
    hasPostGameDemo(slide, questionId = null) {
      if (!slide) return false

      const targetId = questionId || slide.pageId || slide.page || slide.id
      if (!targetId) return false

      // Check in the local list of existing DB recordings first
      if (this.existingDemoQuestionIds && this.existingDemoQuestionIds.some((id) => String(id) === String(targetId))) {
        return true
      }

      // Check across all completed gameOnRecords postGameDemos
      return this.completedGameOnRecords.some((rec) => rec.postGameDemos && rec.postGameDemos.some((id) => String(id) === String(targetId)))
    },
    // Computes dynamic level stats for CPA questions
    getCpaBloomStats(slide) {
      const fullQuestion = this.getFullQuestion(slide)
      const BloomLabels = ['REMEMBER', 'UNDERSTAND', 'APPLY', 'ANALYZE', 'EVALUATE', 'CREATE']

      let questionsToProcess = []

      if (fullQuestion && fullQuestion.bloom_questions) {
        Object.entries(fullQuestion.bloom_questions).forEach(([key, qId]) => {
          questionsToProcess.push({
            id: qId,
            bloomValue: parseInt(key) + 1,
          })
        })
      } else {
        questionsToProcess.push({
          id: fullQuestion ? fullQuestion._id || fullQuestion.id || slide.page : slide.page,
          bloomValue: fullQuestion && fullQuestion.bloom && fullQuestion.bloom.length > 0 ? fullQuestion.bloom[0] : 4,
        })
      }

      const allMembers = this.getSlideMembers(slide)

      return questionsToProcess.map((q) => {
        let attemptCount = 0
        let correctHits = 0
        let stuckCount = 0

        allMembers.forEach((m) => {
          const isRootQuestion = fullQuestion && (q.id === fullQuestion._id || q.id === fullQuestion.id)

          const isAttempted =
            (m.lockedPages && m.lockedPages.includes(q.id)) ||
            (isRootQuestion && m.lockedPages && m.lockedPages.some((pId) => pId === slide.page || pId === slide.id)) ||
            (m.lockedQuestionIds && m.lockedQuestionIds.includes(q.id))

          const isCorrect =
            (m.correctPages && m.correctPages.includes(q.id)) ||
            (isRootQuestion && m.correctPages && m.correctPages.some((pId) => pId === slide.page || pId === slide.id)) ||
            (m.correctQuestionIds && m.correctQuestionIds.includes(q.id))

          const hasStuckLog =
            this.activityLogs &&
            this.activityLogs.some(
              (l) => (l.pageId === q.id || l.questionId === q.id || (isRootQuestion && (l.pageId === slide.page || l.pageId === slide.id))) && l.uid === m.uid && l.isStuck
            )
          const isStuck = hasStuckLog && !isCorrect

          if (isAttempted) {
            attemptCount++
          }
          if (isCorrect) {
            correctHits++
          }
          if (isStuck) {
            stuckCount++
          }
        })

        let wrongHits = attemptCount - correctHits
        let bloomValue = q.bloomValue
        let levelName = BloomLabels[bloomValue - 1] || 'ANALYZE'
        let levelPrefix = `LEVEL ${bloomValue} : `

        const hasDemo = this.hasPostGameDemo(slide, q.id)

        return {
          questionId: q.id,
          levelPrefix: levelPrefix,
          levelName: levelName,
          levelNum: bloomValue,
          levelNameFormated: levelName.charAt(0).toUpperCase() + levelName.slice(1).toLowerCase(),
          attempt: attemptCount,
          correct: correctHits,
          stuck: stuckCount,
          wrong: wrongHits,
          hasDemo: !!hasDemo,
        }
      })
    },
    getPercentAttempted(slide) {
      const allMembers = this.getSlideMembers(slide)
      if (allMembers.length === 0) return 0

      const pageId = slide.pageId || slide.page || slide.id
      const normPageId = this.getNormId(pageId)
      let attemptedCount = 0
      allMembers.forEach((m) => {
        const isAttempted =
          (m.lockedPages && (m.lockedPages.includes(pageId) || (normPageId && m.lockedPages.some((lp) => this.getNormId(lp) === normPageId)))) ||
          (m.lockedQuestionIds && (m.lockedQuestionIds.includes(pageId) || (normPageId && m.lockedQuestionIds.some((lq) => this.getNormId(lq) === normPageId))))
        if (isAttempted) attemptedCount++
      })
      return Math.round((attemptedCount / allMembers.length) * 100)
    },
    getPercentStuck(slide) {
      const allMembers = this.getSlideMembers(slide)
      if (allMembers.length === 0) return 0

      const pageId = slide.pageId || slide.page || slide.id
      const normPageId = this.getNormId(pageId)
      let stuckCount = 0
      allMembers.forEach((m) => {
        const hasStuckLog =
          this.activityLogs &&
          this.activityLogs.some((l) => (l.pageId === pageId || (normPageId && this.getNormId(l.pageId) === normPageId)) && l.uid === m.uid && l.isStuck)
        const hasCorrectAnswer =
          (m.correctPages && (m.correctPages.includes(pageId) || (normPageId && m.correctPages.some((cp) => this.getNormId(cp) === normPageId)))) ||
          (m.correctQuestionIds && (m.correctQuestionIds.includes(pageId) || (normPageId && m.correctQuestionIds.some((cq) => this.getNormId(cq) === normPageId))))
        if (hasStuckLog && !hasCorrectAnswer) stuckCount++
      })
      return Math.round((stuckCount / allMembers.length) * 100)
    },
    getPercentCorrect(slide) {
      const allMembers = this.getSlideMembers(slide)
      if (allMembers.length === 0) return 0

      const pageId = slide.pageId || slide.page || slide.id
      const normPageId = this.getNormId(pageId)
      let correctHits = 0
      allMembers.forEach((m) => {
        const isCorrect =
          (m.correctPages && (m.correctPages.includes(pageId) || (normPageId && m.correctPages.some((cp) => this.getNormId(cp) === normPageId)))) ||
          (m.correctQuestionIds && (m.correctQuestionIds.includes(pageId) || (normPageId && m.correctQuestionIds.some((cq) => this.getNormId(cq) === normPageId))))
        if (isCorrect) correctHits++
      })
      return Math.round((correctHits / allMembers.length) * 100)
    },
    getPercentWrong(slide) {
      const attempted = this.getPercentAttempted(slide)
      const correct = this.getPercentCorrect(slide)
      return Math.max(0, attempted - correct)
    },
    // Handles clicking the SHOW DEMO button. Replays if video exists, records if not.
    async handleShowDemo(slide, stat, forceRecord = false) {
      if (!slide) return
      
      this.isLoading = true;

      const questionId = stat ? stat.questionId : (slide.page || slide.id)
      const teacherUid = this.user?._id || this.$store?.state?.user?._id

      // Check if recording already exists in DB
      let existingRecording = null
      if (!forceRecord && window.App) {
        try {
          const res = await App.service('review-recording').find({
            query: {
              sid: this.session?.sid,
              uid: teacherUid,
              questionId: questionId,
              type: 'analyticsDemo',
              $limit: 1
            }
          })
          const recordings = res.data || res || []
          if (recordings.length > 0) {
            existingRecording = recordings[0]
          }
        } catch (e) {
          console.error('Failed to query review-recording:', e)
        }
      }

      if (existingRecording && existingRecording.video) {
        const hashToUrl = (window.Fn && window.Fn.hashToUrl) || ((h) => h)
        this.previewVideoUrl = hashToUrl(existingRecording.video)
      } else {
        this.openPostGameDemo(slide, stat)
      }
      this.isLoading = false;
    },
    handleShowDemoFromChild({slide, stat, forceRecord}) {
      this.handleShowDemo(slide, stat, forceRecord)
    },
    previewImage(picUrl) {
      if (picUrl) {
        const hashToUrl = (window.Fn && window.Fn.hashToUrl) || ((h) => h)
        window.open(hashToUrl(picUrl), '_blank')
      }
    },
    openPostGameDemo(slide, stat) {
      if (!slide) return

      const pageId = slide.page || slide.id || slide.pageId || slide.page_id
      const fullQuestion = this.getFullQuestion(slide) || {}

      // If this is a CPA level, fetch the specific level's question details
      const targetQId = stat ? stat.questionId : pageId
      const session = this.$store && this.$store.state && this.$store.state.session
      const questions = session && session.questions ? session.questions : []
      const targetQuestion = questions.find((q) => q._id === targetQId || q.id === targetQId || q.page === targetQId) || fullQuestion

      let pic = slide.pic || targetQuestion.pic || fullQuestion.pic || ''
      if (!pic && this.$store && this.$store.getters && this.$store.getters.getPageById) {
        const normalizedPageId = pageId && typeof pageId === 'string' && pageId.startsWith('p-') ? pageId.replace('p-', '') : pageId
        const pageData = this.$store.getters.getPageById(pageId) || this.$store.getters.getPageById(normalizedPageId)
        if (pageData && pageData.pic) {
          pic = pageData.pic
        }
      }

      const baseQuestion = this.getFullQuestion(slide) || {}
      this.selectedStudentSlide = {
        ...slide,
        ...targetQuestion,
        _id: stat ? stat.questionId : targetQuestion._id || slide._id || slide.page || slide.id,
        id: stat ? stat.questionId : slide.page || slide.id || slide.pageId,
        page: stat ? stat.questionId : slide.page || slide.id || slide.pageId,
        isPostGameDemoCpa: !!stat,
        bloom_questions: baseQuestion.bloom_questions || baseQuestion.cpa?.bloom_questions,
        pic,
      }
      this.activeCpaStat = stat
      this.isRedoDemo = this.hasPostGameDemo(slide, stat ? stat.questionId : null)

      const targetUids = []
      const classMembers =
        (this.$store && this.$store.state && this.$store.state.teacher && this.$store.state.teacher.rooms && this.$store.state.teacher.rooms.members) || []

      if (classMembers.length > 0) {
        classMembers.forEach((m) => {
          targetUids.push(m._id || m.uid || m.id)
        })
      } else if (this.gameOnRecord && this.gameOnRecord.members) {
        this.gameOnRecord.members.forEach((m) => {
          targetUids.push(m.uid)
        })
      }

      this.groupDemoUids = targetUids
      this.currentRecordingType = 'analyticsDemo'
      this.isPostGameDemo = true
      this.startDemoType = 'analyticsDemo'
      this.showStartDemoPopup = true
    },
    async onStartDemo() {
      this.showStartDemoPopup = false
      if (!this.isMeetingOngoing) {
        await this.handleZoomClick()
      }
      this.showStudentSlideView = true
    },
    closeStudentSlideView() {
      this.showStudentSlideView = false
    },
    async handleDemoSent(question) {
      if (!question) return

      const pageId = question._id || question.id || question.questionId || question.page || question.pageId
      if (!this.gameOnRecord) return

      let postGameDemos = this.gameOnRecord.postGameDemos ? [...this.gameOnRecord.postGameDemos] : []
      if (!postGameDemos.some((id) => String(id) === String(pageId))) {
        postGameDemos.push(pageId)

        this.$set(this.gameOnRecord, 'postGameDemos', postGameDemos)

        if (window.App && this.gameOnRecord._id) {
          try {
            await App.service('game-on').patch(this.gameOnRecord._id, {
              postGameDemos,
            })
            console.log('DEBUG: Patched postGameDemos:', postGameDemos)
          } catch (err) {
            console.error('Failed to update postGameDemos:', err)
          }
        }
      }
      this.fetchExistingDemos()
    },
    async fetchExistingDemos() {
      const teacherUid = this.user?._id || this.$store?.state?.user?._id
      const sid = this.session?.sid
      if (window.App && sid) {
        try {
          const query = {
            sid: sid,
            $limit: 1000,
          }
          if (teacherUid) {
            query.uid = teacherUid
          }
          const res = await App.service('review-recording').find({ query })
          const recordings = res.data || res || []
          this.existingDemoQuestionIds = recordings.map(r => r.questionId).filter(Boolean)
        } catch (e) {
          console.error('Failed to fetch existing review-recordings:', e)
        }
      }
    },
    async handleZoomClick() {
      if (this.session && this.session.sid) {
        localStorage.setItem('meeting_active_' + this.session.sid, 'true')
        localStorage.removeItem('meeting_stopped_at_' + this.session.sid)
      }
      await this.$store.dispatch('upSession', {lastMeetingHeartbeat: Date.now()})
      const userName = this.user?.nickname || this.user?.name || ''
      this.$store.commit('setMeeting', {
        active: true,
        minimized: false,
        name: userName,
        sid: this.session.sid,
        sessionId: this.session._id,
        audioMuted: true,
        videoMuted: true,
        isTeacher: true,
      })
    },
    getStudentName(studentId) {
      return ''
    },
    markLogAsResolved() {}
  },
}
</script>

<style scoped>
.ga-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: #f8fafc;
  z-index: 99999;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  display: flex;
  flex-direction: column;
}

.ga-container-card {
  width: 100%;
  height: 100%;
  background: #f8fafc;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.ga-header {
  width: 100%;
  background: #0f172a;
  color: #ffffff;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
}

.ga-header-content {
  width: 100%;
  padding: 16px 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.ga-header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.ga-game-mode-badge {
  background: #10b981;
  color: #ffffff;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
}

.ga-header-title {
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.5px;
}

.ga-close-btn {
  color: #94a3b8;
  font-size: 20px;
  cursor: pointer;
  transition: color 0.2s;
}

.ga-close-btn:hover {
  color: #ffffff;
}

.ga-body {
  width: 100%;
  flex: 1;
  overflow-y: auto;
  padding: 24px 0;
}

.ga-subpage-body {
  padding: 0 !important;
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden !important;
}

.ga-content-wrapper {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
}

.ga-alert-banner {
  background: #fffbeb;
  border: 1.5px solid #fef3c7;
  border-radius: 8px;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 24px;
}

.ga-alert-icon {
  color: #d97706;
  font-size: 14px;
}

.ga-alert-text {
  color: #b45309;
  font-size: 13px;
  font-weight: 600;
}

/* Questions Overview Card */
.ga-overview-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.ga-overview-info {
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1.2;
}

.ga-badge-num {
  background: #334155;
  color: #ffffff;
  font-weight: 800;
  font-size: 14px;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  flex-shrink: 0;
}

.ga-overview-title {
  font-size: 16px;
  font-weight: 600;
  color: #0f172a;
  margin: 0;
}

.ga-overview-subtitle {
  font-size: 14px;
  color: #64748b;
  margin: 2px 0 0 0;
}

.ga-stats-container {
  display: flex;
  align-items: center;
  gap: 28px;
  flex: 2;
  justify-content: center;
}

.ga-stat-item {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.ga-stat-num {
  font-size: 20px;
  font-weight: 700;
  color: #0f172a;
}

.ga-stat-label {
  font-size: 10px;
  font-weight: 500;
  color: #64748b;
  letter-spacing: 0.5px;
  margin-top: 4px;
}

.ga-stat-divider {
  width: 1px;
  height: 44px;
  background: #e2e8f0;
}

.text-success {
  color: #10b981 !important;
  font-weight: 500;
}

.text-danger {
  color: #ef4444 !important;
}



.ga-overview-actions {
  flex: 0.8;
  display: flex;
  justify-content: flex-end;
}

.ga-btn-outline {
  background: #ffffff;
  border: 1px solid #1e293b;
  color: #1e293b;
  font-weight: 500;
  font-size: 14px;
  padding: 10px 20px;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s;
}

.ga-btn-outline:hover {
  background: #f8fafc;
  border-color: #0f172a;
  color: #0f172a;
}

/* Slide details section */
.ga-slide-list-section {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.ga-section-header {
  margin-bottom: 16px;
}

.ga-section-header h3 {
  margin: 0;
  font-size: 15px;
  font-weight: 800;
  color: #0f172a;
}

.ga-section-header p {
  margin: 4px 0 0 0;
  font-size: 12px;
  color: #64748b;
}

.ga-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.ga-slide-row {
  display: flex;
  align-items: center;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 14px 16px;
  cursor: pointer;
  transition: all 0.2s;
}

.ga-slide-row:hover {
  background: #f1f5f9;
  border-color: #cbd5e1;
}

.ga-slide-num {
  width: 32px;
  height: 32px;
  background: #e2e8f0;
  color: #475569;
  font-weight: 700;
  font-size: 12px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 6px;
  margin-right: 16px;
}

.ga-slide-thumb {
  width: 80px;
  height: 48px;
  background-color: #e2e8f0;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  border-radius: 6px;
  margin-right: 16px;
}

.ga-slide-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.ga-slide-title {
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
}

.ga-slide-desc {
  font-size: 11px;
  color: #64748b;
}

.ga-slide-prog-wrap {
  width: 120px;
  margin-right: 24px;
}

.ga-prog-bg {
  width: 100%;
  height: 6px;
  background: #e2e8f0;
  border-radius: 3px;
  overflow: hidden;
}

.ga-prog-fill {
  height: 100%;
  border-radius: 3px;
}

.ga-slide-tag {
  font-size: 9px;
  font-weight: 800;
  padding: 4px 12px;
  border: 1.5px solid;
  border-radius: 12px;
  letter-spacing: 0.5px;
  width: 80px;
  text-align: center;
}

.ga-chevron i {
  font-size: 14px;
  color: #94a3b8;
  margin-left: 20px;
}

/* Tasks Section */
.ga-tasks-header {
  margin-bottom: 20px;
  margin-top: 8px;
}

.ga-tasks-header-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
}

.ga-tasks-title {
  font-size: 24px;
  font-weight: 600;
  color: #0f172a;
  margin: 0;
}

.ga-tasks-subtitle {
  font-size: 16px;
  color: #64748b;
  margin: 6px 0 0 0;
}

.ga-tasks-count {
  font-size: 14px;
  color: #64748b;
  font-weight: 500;
}

.ga-tasks-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin-bottom: 12px;
}

.ga-task-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-height: 280px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
}

.ga-task-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.ga-status-badge {
  font-size: 10px;
  font-weight: 700;
  padding: 4px 10px;
  border-radius: 6px;
  letter-spacing: 0.5px;
}

.badge-success {
  background: #d1fae5;
  color: #065f46;
}

.badge-gray {
  background: #f1f5f9;
  color: #475569;
}

.badge-warning {
  background: #fef3c7;
  color: #92400e;
}

.ga-task-body {
  display: flex;
  flex-direction: column;
}

.ga-task-title {
  font-size: 16px;
  font-weight: 600;
  color: #0f172a;
  margin: 0 0 2px 0;
}

.ga-task-desc {
  font-size: 13px;
  color: #64748b;
  margin: 0 0 20px 0;
  line-height: 1.4;
}

.ga-progress-section {
  margin-top: 12px;
}

.ga-progress-labels {
  display: flex;
  justify-content: space-between;
  font-size: 11px;
  color: #475569;
  margin-bottom: 6px;
}

.ga-fraction {
  font-weight: 600;
}

.ga-progress-bar {
  width: 100%;
  height: 8px;
  background: #f1f5f9;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 6px;
}

.ga-progress-fill {
  height: 100%;
  border-radius: 4px;
}

.bg-success {
  background: #10b981;
}

.bg-gray {
  background: #cbd5e1;
}

.bg-warning {
  background: #f59e0b;
}

.ga-progress-footer,
.ga-progress-footer span {
  font-size: 12px !important;
  color: #94a3b8 !important;
  font-weight: 600 !important;
}

.ga-task-footer {
  margin-top: 20px;
}

.ga-task-btn {
  width: 100%;
  background: #ffffff;
  border: 1px solid #1e293b;
  color: #1e293b;
  font-weight: 500;
  font-size: 14px;
  padding: 12px 24px;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.ga-task-btn:hover {
  background: #f8fafc;
  border-color: #0f172a;
  color: #0f172a;
}

.ga-btn-arrow {
  font-weight: 800 !important;
  font-size: 18px !important;
  line-height: 1;
}

/* Animations */
.slide-fade-enter-active, .slide-fade-leave-active {
  transition: all 0.3s ease;
}
.slide-fade-enter, .slide-fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

::-webkit-scrollbar {
  width: 8px;
}
::-webkit-scrollbar-track {
  background: transparent;
}
::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 4px;
}
::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

/* Responsive CSS */
@media (max-width: 992px) {
  .ga-tasks-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .ga-overlay {
    padding: 0;
    align-items: flex-start;
  }

  .ga-body {
    padding: 16px 0;
  }

  .ga-content-wrapper {
    padding: 0 16px;
  }

  .ga-overview-card {
    flex-direction: column;
    align-items: flex-start;
    padding: 16px;
    gap: 16px;
  }

  .ga-overview-info {
    width: 100%;
  }

  .ga-stats-container {
    width: 100%;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
  }

  .ga-stat-divider {
    display: none;
  }

  .ga-overview-actions {
    width: 100%;
    justify-content: flex-start;
  }

  .ga-btn-outline {
    width: 100%;
  }

  .ga-tasks-header-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .ga-tasks-grid {
    grid-template-columns: 1fr;
  }

  .ga-slide-row {
    flex-wrap: wrap;
    gap: 12px;
  }

  .ga-slide-prog-wrap {
    width: 100%;
    margin-right: 0;
    order: 3;
  }

  .ga-chevron {
    display: none;
  }
}

/* CPA Dashboard card styles copied from GameTrackingPage */
.dva-grid {
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
  padding: 0 24px;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
}

.gt-dashboard-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  height: auto;
}

.gt-dashboard-card-header {
  padding: 16px 20px;
  border-bottom: 1px solid #f1f5f9;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #ffffff;
}

.gt-cpa-card-title {
  font-weight: bold;
  font-size: 13px;
  color: #334155;
}

.gt-cpa-header-stats {
  display: flex;
  gap: 12px;
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
}

.gt-stat-attempted {
  color: #64748b;
}

.gt-stat-stuck {
  color: #eab308;
}

.gt-stat-wrong {
  color: #ef4444;
}

.gt-dashboard-card-body {
  padding: 0;
  display: flex;
  flex-direction: column;
  flex: 1;
}

.gt-bloom-table-container {
  width: 100%;
  overflow-x: auto;
}

.gt-bloom-table {
  width: 100%;
  min-width: 420px;
  border-collapse: collapse;
}

.gt-bloom-header {
  display: flex;
  padding: 12px 20px;
  font-size: 10px;
  font-weight: 700;
  color: #94a3b8;
  letter-spacing: 0.5px;
}

.gt-bloom-row {
  display: flex;
  padding: 12px 20px;
  border-bottom: 1px solid #f1f5f9;
  align-items: center;
}

.gt-bloom-row:last-child {
  border-bottom: none;
}

.gt-cpa-table-header {
  padding: 16px 20px 8px 20px !important;
}

.gt-cpa-table-header > div {
  color: #64748b !important;
  font-weight: 600 !important;
  font-size: 11px !important;
  letter-spacing: 0.5px !important;
}

.gt-cpa-rows {
  gap: 10px !important;
  display: flex;
  flex-direction: column;
  padding: 0 20px 20px 20px !important;
}

.gt-cpa-row {
  background: #f8fafc !important;
  border: none !important;
  border-radius: 8px !important;
  padding: 12px 0 !important;
  display: flex;
  align-items: center;
  width: 100%;
}

.gt-cpa-col-level {
  display: flex;
  align-items: center;
  gap: 8px;
  border-top-left-radius: 8px !important;
  border-bottom-left-radius: 8px !important;
}

.gt-cpa-level-badge {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #64748b;
  color: #ffffff;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 10px;
  font-weight: 700;
}

.gt-cpa-level-name {
  font-size: 13px;
  font-weight: 500;
  color: #1e293b;
}

.gt-cpa-col-stat {
  font-size: 12px !important;
  font-weight: 600 !important;
  text-align: center;
}

.gt-cpa-view-btn {
  background: transparent;
  border: 1px solid #cbd5e1;
  color: #475569;
  font-weight: 600;
  font-size: 11px;
  padding: 6px 16px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  letter-spacing: 0.5px;
}

.gt-cpa-view-btn:hover {
  background: #f8fafc;
  border-color: #94a3b8;
  color: #1e293b;
}

.gt-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.4);
  z-index: 99999;
  display: flex;
  justify-content: center;
  align-items: center;
}
</style>
